// useFakeSplashScreen.ts
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Platform, AppState } from "react-native";
import UseUpdates from "./UseUpdates";
import { loadAsync } from "expo-font";
import Bugsnag from "@bugsnag/expo";

import useRefreshUser from "./useRefreshUser";
import { getCmsData } from "../client/cms";
import { useSetAtom } from "jotai";
import { cmsDataAtom } from "../atoms";

interface UseFakeSplashScreenProps {
  setShowFakeSplashScreen: Dispatch<SetStateAction<Boolean>>;
}

/**
 * Run some routines whilst holding the user at the fake splash screen.
 * This is used to check for updates and load fonts before the app is ready.
 *
 * @param {Function} setShowFakeSplashScreen - Function to control the visibility of the fake splash screen.
 *
 * @returns { Boolean } finishedAppSetup - True when app setup is complete - e.g. font loading, user refresh
 *
 **/
export const useFakeSplashScreen = ({
  setShowFakeSplashScreen,
}: UseFakeSplashScreenProps): { finishedAppSetup: Boolean } => {
  // don't run at all on web...
  if (Platform.OS === "web") return { finishedAppSetup: false };

  // this is used later in setupApp
  const { refreshUser } = useRefreshUser();

  // Destructure update functions from UseUpdates hook
  const { isUpdateAvailable, checkForAndApplyUpdateAsync } = UseUpdates();

  // set cms data
  const setCmsData = useSetAtom(cmsDataAtom);

  const [finishedAppSetup, setFinishedAppSetup] = useState(false);

  useEffect(() => {
    console.log("**** finishedAppSetup? ", finishedAppSetup);
    if (finishedAppSetup) setShowFakeSplashScreen(false);
  }, [finishedAppSetup]);

  // checks for and downloads update. this is ran in sequence
  // further down.
  const checkForUpdate = async () => {
    Bugsnag.leaveBreadcrumb("update check start");
    const updateAvailable = await isUpdateAvailable();

    if (updateAvailable) {
      Bugsnag.leaveBreadcrumb("update check update available");
      await checkForAndApplyUpdateAsync();
      Bugsnag.leaveBreadcrumb("update check finished - update applied");
    } else {
      Bugsnag.leaveBreadcrumb("update check finished - no update");
    }
  };

  // (2) Cold start update check
  useEffect(() => {
    console.log("[UseFakeSplash] ****** Cold start ******");
    // Helper function to load fonts asynchronously
    const loadFontsAsync = async () => {
      try {
        Bugsnag.leaveBreadcrumb("font load started");
        const fontsModule = await import("./../styles/fonts");

        await loadAsync(fontsModule.FONTS);
        console.log("[Setup] Fonts Loaded ");
        Bugsnag.leaveBreadcrumb("font load finished");
      } catch (err) {
        console.error("Error loading fonts.... ", err);
      }
    };

    // Note - this is ios and android only. if
    // any of these setup functions are needed for web they
    // need to be done separately.
    const setupApp = async () => {
      // Check for updates and apply first and foremost
      try {
        await checkForUpdate();
      } catch (error) {
        console.error("[useFakeSplash] error with checkForUpdate: ", error);
        Bugsnag.notify("[useFakeSplash] error with checkForUpdate: ", error);
      }

      await Promise.all([
        loadFontsAsync(),
        refreshUser(),
        getCmsData()
          .then((res) => {
            setCmsData(res);
            console.log("**** got cms data");
          })
          .catch((e) => console.log("*** issue fetching cms data...", e)),
        // sleep for 3 secs
        await new Promise((r) => setTimeout(r, 3000)),
      ]);
      console.log("[useFakeSplash] all data fetched...");
      // setFinishedAppSetup(true);
    };

    // TODO - log bugsnag error here..
    setupApp()
      .catch((error) => {
        console.log("[UseFakeSplash] Error during setup: ", error);
        Bugsnag.notify("[UseFakeSplash] Error during setup: ", error);
      })
      .finally(() => setFinishedAppSetup(true));
  }, []);

  // (3) Warm start logic: listen to app state changes and check for updates when the app becomes active.
  useEffect(() => {
    const subscription = AppState.addEventListener(
      "change",
      async (nextAppState) => {
        console.log("[UseFakeSplash] ****** WARM START ******");
        console.log("[UseFakeSplash] AppState changed: ", nextAppState);
        // When the app comes back to the foreground, check for updates and
        // if so - show splash screen, apply update and hide splash screen.
        if (nextAppState === "active") {
          console.log("[UseFakeSplash] ****** Warm start ******");
          console.log("[UseFakeSplash] checking for update");

          await checkForUpdate();

          console.log("[UseFakeSplash] finished checking for update");

          setShowFakeSplashScreen(false);
          setFinishedAppSetup(true);
        } else {
          // If the app goes to the background, you might want to show the splash screen again.
          console.log(
            "[UseFakeSplash] AppState changed - got a different state: ",
            nextAppState
          );
        }
      }
    );

    // Cleanup subscription on unmount
    return () => subscription.remove();
  }, []);

  return { finishedAppSetup };
};
